/**
 * @file The page-level notice for form point-in-time recovery (SYD-2450), in
 * one slot with two variants: the form is showing a restore point read-only, or
 * a restore has just landed. Mounted by `FormPageContent` inside the sticky div
 * that holds `FormControl`, so it travels with the form bar rather than
 * scrolling away.
 *
 * The preview variant renders for exactly as long as
 * `formRecoveryStore.state.previewing` hands `FormUI` its `forceDisabled` — including
 * when there is no result to show. A `previewing` page with nothing on screen is the
 * worst state this feature can reach, a form gone read-only with no banner and no way
 * back, so the variant renders on the flag alone and words the missing read.
 *
 * The restored variant holds while the entry its restore recorded is still what the
 * undo stack's next press would reach. That is what makes the undo true to its name:
 * `formUndoStack` undoes the top of the stack and nothing else, so a field typed
 * after the restore would otherwise be what the button reverted. When a newer entry
 * lands — or the undo itself moves the entry to the redo stack — the notice has
 * nothing left to offer and goes. A restore that recorded no entry at all (references
 * only, which the stack cannot revert) holds until dismissed with the button dead.
 *
 * A restore covers the form version and the rows of every table of the moment, and the
 * notice accounts for both, because the two do not undo alike. `restoredFrom.count` is
 * the version document's keys, and a restore that changed only rows writes no version
 * document at all — so counting from it alone would announce "Restored 0 fields" over a
 * hundred rewritten rows. The title counts both sides; the description says what
 * happened to the rows in the confirmation's own verbs, names the locked rows the
 * operator chose to proceed without, and — when the press is live and would leave those
 * rows behind — says so. Rows are undone by `arTableRowUndo` through the
 * `trashOperationId` each table reported, a separate mechanism this button does not
 * drive, so the label reads `Undo restore` only while there are no restored rows for it
 * to leave behind.
 *
 * The confirmation is mounted unconditionally beside the two variants: a
 * successful restore ends the preview, and an overlay unmounted by that flip
 * would lose its own closing transition.
 *
 * `useEvaluateFormAccess` is mounted with `enabled` tied to the selection, so a
 * form page nobody has opened the recovery group on costs no extra read of the
 * form document — and the answer is already in by the time a preview starts.
 *
 * The undo re-reads its gate at CLICK time rather than trusting the render
 * that drew the button: the handler compares the recorded entry id against
 * `formUndoStack.availability.undo.entryId` off the live proxy, so which entry
 * gets reverted never depends on when React last re-rendered. The snapshot read
 * of the same leaf stays, because the button's disabled state has to move with it.
 */

import { FC, useState } from 'react';
import { Alert, Button, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';

import { formRecoveryStore, formStore } from '../../../store';
import { formUndoStack } from '../../../store/form';
import { useEvaluateFormAccess } from '../../../use/data/useEvaluateFormAccess';
import { useFormRecoveryList } from '../../../use/form/useFormRecoveryList';
import { useFormUndoDispatch } from '../../../use/form/useFormUndoDispatch';
import FormRecoveryConfirmModal from './FormRecoveryConfirmModal';
import { restorableRowCounts } from './restorableRowCounts';

const MOMENT_FORMAT = 'MMM D, YYYY h:mm:ss A';

/** `1 field`, `3 fields` — the noun follows the count. */
const countPhrase = (count: number, noun: string): string =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

const FormRecoveryBanner: FC = () => {
  const formSnap = useSnapshot(formStore.state);
  const formId = formSnap.formId;
  const versionId = formSnap.versionId;

  const snap = useSnapshot(formRecoveryStore.state);
  const previewing = snap.previewing;
  const selectedCursorMicros = snap.selectedCursorMicros;
  const result = snap.result;
  const tableReads = snap.tableReads;
  const restoredFrom = snap.restoredFrom;

  const undoSnap = useSnapshot(formUndoStack.availability.undo);
  const undoTopEntryId = undoSnap.entryId;

  /**
   * Enabled from the selection, not from `previewing`. `useEvaluateFormAccess`
   * answers `NO_ACCESS` while disabled and re-fetches the form document when the
   * flag turns on, so gating on `previewing` left `Restore this moment` dead for
   * the length of that fetch — with a tooltip blaming write access the operator
   * has. Selecting a row happens a server round trip before the preview does, so
   * starting the check there closes the window and a form page nobody has opened
   * the recovery group on still pays nothing.
   */
  const formAccess = useEvaluateFormAccess({
    formId,
    versionId,
    enabled: previewing || selectedCursorMicros !== null,
  });
  const dispatchUndo = useFormUndoDispatch();
  const { refresh } = useFormRecoveryList();
  const [confirmOpen, setConfirmOpen] = useState(false);

  /**
   * `null` means the button is live. A missing result comes first: a preview showing
   * nothing has no version to write back, and a table still out is read the same way,
   * because a count missing its other half is no better than no count at all.
   *
   * One restore covers the form and its tables, so the gate under it counts both. A
   * moment at which only rows changed leaves the version `unchanged`, and refusing on
   * that outcome alone told the operator there was nothing to restore while the
   * timeline beside them named the table that had changed.
   */
  const differingValueCount = result?.outcome === 'version' ? result.diff.length : 0;

  const rowCounts = restorableRowCounts(tableReads);

  const restoreDisabledReason =
    result === null || tableReads.some((read) => read.status === 'reading')
      ? 'Read this restore point before restoring.'
      : differingValueCount + rowCounts.writable === 0
        ? rowCounts.differing > 0
          ? 'Every row that differs here is locked, so this restore has nothing it can write.'
          : 'This restore point matches the form as it stands now, so there is nothing to restore.'
        : !formAccess.canWrite
          ? 'Restoring needs write access to a draft version.'
          : null;

  const restoreIsUndoTop =
    restoredFrom !== null &&
    restoredFrom.undoEntryId !== null &&
    restoredFrom.undoEntryId === undoTopEntryId;

  const showRestored =
    !previewing &&
    restoredFrom !== null &&
    (restoredFrom.undoEntryId === null || restoreIsUndoTop);

  /**
   * What the restore did, on both sides of the one transaction. `count` is the version
   * document's keys and is `0` on a restore that changed only rows — which writes no
   * version document at all — so the rows are counted beside it rather than from it,
   * and such a restore never reads as nothing having happened.
   */
  const restoredTables = restoredFrom?.tables ?? [];
  const rowsChanged = restoredTables.reduce((total, table) => total + table.restored.edit, 0);
  const rowsBack = restoredTables.reduce((total, table) => total + table.restored.recreate, 0);
  const rowsGone = restoredTables.reduce((total, table) => total + table.restored.delete, 0);
  const restoredRowCount = rowsChanged + rowsBack + rowsGone;
  const skippedRowCount = restoredTables.reduce((total, table) => total + table.skipped.length, 0);

  const restoredParts = [
    ...(restoredFrom !== null && restoredFrom.count > 0
      ? [countPhrase(restoredFrom.count, 'field')]
      : []),
    ...(restoredRowCount > 0 ? [countPhrase(restoredRowCount, 'row')] : []),
  ];
  const restoredWhat = restoredParts.length === 0 ? 'nothing' : restoredParts.join(' and ');

  /**
   * The three things the title cannot carry: what happened to the rows, in the verbs
   * the confirmation used for them a moment earlier; the rows this restore was told to
   * leave behind, which now stand at the present while everything around them stands at
   * the retained moment; and the reach of the button beside them. The undo entry covers
   * the version's fields alone, so a restore that wrote rows must not offer a press that
   * reads as putting them back.
   */
  const rowPhrases = [
    ...(rowsChanged > 0 ? [`${countPhrase(rowsChanged, 'row')} changed`] : []),
    ...(rowsBack > 0 ? [`${countPhrase(rowsBack, 'row')} came back`] : []),
    ...(rowsGone > 0 ? [`${countPhrase(rowsGone, 'row')} went away`] : []),
  ];
  const rowNotice = rowPhrases.length === 0 ? null : `${rowPhrases.join(', ')}.`;
  const skippedNotice =
    skippedRowCount === 0
      ? null
      : skippedRowCount === 1
        ? '1 locked row was left exactly as it was.'
        : `${skippedRowCount} locked rows were left exactly as they were.`;
  /**
   * The press is live and it would leave the rows behind — the one combination that
   * needs saying. A dead button promises nothing, and its own tooltip already says why.
   */
  const undoLeavesRows = restoredRowCount > 0 && restoreIsUndoTop;
  const rowUndoNotice = undoLeavesRows
    ? 'Undo puts the form values back. The restored rows stay as they are.'
    : null;
  const restoredDescription = [rowNotice, skippedNotice, rowUndoNotice]
    .filter((line): line is string => line !== null)
    .join(' ');

  const undoLabel = undoLeavesRows ? 'Undo the form values' : 'Undo restore';
  const undoAriaLabel = undoLeavesRows
    ? 'Undo the form values this restore wrote'
    : 'Undo the restore that just landed';

  /**
   * The entry is compared again here, off the live proxy, because the render that
   * drew this button may predate an edit that pushed a newer entry on top — and
   * `⌘Z` reverts the top of the stack and nothing else. Both reads happen before
   * the first await, so neither can observe a stack the operator did not act on.
   *
   * The undo then writes a version of its own, so the walk the sidebar holds no
   * longer reaches the newest restore point. Re-walking the head is the only way
   * to show it, and it is billed against an action the operator explicitly took.
   */
  const handleUndoRestore = async (): Promise<void> => {
    const recorded = formRecoveryStore.state.restoredFrom?.undoEntryId ?? null;
    if (recorded === null || recorded !== formUndoStack.availability.undo.entryId) return;
    await dispatchUndo('undo');
    await refresh();
  };

  return (
    <>
      {previewing && (
        <Alert
          banner
          type={result === null ? 'warning' : 'info'}
          showIcon
          aria-label='Showing a restore point'
          title={
            result === null
              ? 'That restore point could not be shown. The form stays read-only until you exit the preview. Nothing has been written.'
              : `Showing this form as it stood at ${dayjs(
                  result.committedAtMillis ?? result.readAtMillis,
                ).format(MOMENT_FORMAT)} — read-only. Nothing has been written.`
          }
          action={
            <Space size={5}>
              <Tooltip title={restoreDisabledReason}>
                <span>
                  <Button
                    size='small'
                    disabled={restoreDisabledReason !== null}
                    onClick={() => setConfirmOpen(true)}
                    aria-label='Restore the moment being shown'
                  >
                    Restore this moment
                  </Button>
                </span>
              </Tooltip>
              <Button
                size='small'
                onClick={() => formRecoveryStore.exitPreview()}
                aria-label='Exit the preview and return to the current form'
              >
                Exit preview
              </Button>
            </Space>
          }
        />
      )}

      {showRestored && restoredFrom !== null && (
        <Alert
          banner
          type='success'
          showIcon
          closable={{
            'aria-label': 'Dismiss the restore notice',
            onClose: () => {
              formRecoveryStore.state.restoredFrom = null;
            },
          }}
          aria-label='A restore landed'
          title={`Restored ${restoredWhat} from ${dayjs(restoredFrom.atMillis).format(
            MOMENT_FORMAT,
          )}.`}
          description={restoredDescription === '' ? undefined : restoredDescription}
          action={
            <Tooltip
              title={
                restoreIsUndoTop
                  ? undefined
                  : 'Nothing this restore wrote can be undone automatically.'
              }
            >
              <span>
                <Button
                  size='small'
                  disabled={!restoreIsUndoTop}
                  onClick={() => void handleUndoRestore()}
                  aria-label={undoAriaLabel}
                >
                  {undoLabel}
                </Button>
              </span>
            </Tooltip>
          }
        />
      )}

      <FormRecoveryConfirmModal open={confirmOpen} onClose={() => setConfirmOpen(false)} />
    </>
  );
};

FormRecoveryBanner.displayName = 'FormRecoveryBanner';

export default FormRecoveryBanner;
